
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Play, Users, Folder, Heart, User, LogOut, HelpCircle, Info, Book, Key, Settings, Menu, X } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";

import {
  NavigationMenu,
  NavigationMenuList,
  NavigationMenuItem,
} from "@/components/ui/navigation-menu";

import NavbarUserMenu from './NavbarUserMenu';
import { useIsMobile } from '@/hooks/use-mobile';

export const Navbar = () => {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const { user, signOut } = useAuth();
  const isMobile = useIsMobile();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };
  
  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 w-full transition-all duration-300 
        ${scrolled 
          ? 'bg-white/90 dark:bg-youtube-dark/95 backdrop-blur-sm shadow-md border-b border-gray-200 dark:border-gray-800' 
          : 'bg-white dark:bg-youtube-dark'
        }`}
    >
      <div className="max-w-7xl mx-auto px-4 flex items-center justify-between h-16">
        <div className="flex items-center gap-2">
          <Link to="/" className="flex items-center gap-2 font-bold text-lg">
            <svg viewBox="0 0 24 24" className="w-8 h-8 text-youtube-red fill-current">
              <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z" />
            </svg>
            <span className="hidden sm:inline">PersonalTube</span>
          </Link>
        </div>
        
        {/* Mobile menu trigger */}
        {isMobile && (
          <button 
            onClick={toggleMobileMenu}
            className="md:hidden flex items-center"
            aria-label={mobileMenuOpen ? "Fermer le menu" : "Ouvrir le menu"}
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        )}
        
        {/* Desktop navigation */}
        <nav className="hidden md:flex items-center justify-center flex-1">
          {user ? (
            <ul className="flex items-center space-x-1 md:space-x-4">
              <li>
                <Link 
                  to="/" 
                  className={`nav-link ${isActive('/') ? 'tube-button-active' : ''}`}
                >
                  <Home className="w-5 h-5" />
                  <span className="hidden md:inline">Accueil</span>
                </Link>
              </li>
              <li>
                <Link 
                  to="/feed" 
                  className={`nav-link ${isActive('/feed') ? 'tube-button-active' : ''}`}
                >
                  <Play className="w-5 h-5" />
                  <span className="hidden md:inline">Flux</span>
                </Link>
              </li>
              <li>
                <Link 
                  to="/creators" 
                  className={`nav-link ${isActive('/creators') ? 'tube-button-active' : ''}`}
                >
                  <Users className="w-5 h-5" />
                  <span className="hidden md:inline">Créateurs</span>
                </Link>
              </li>
              <li>
                <Link 
                  to="/playlists" 
                  className={`nav-link ${isActive('/playlists') ? 'tube-button-active' : ''}`}
                >
                  <Folder className="w-5 h-5" />
                  <span className="hidden md:inline">Collections</span>
                </Link>
              </li>
              <li>
                <Link 
                  to="/settings" 
                  className={`nav-link ${isActive('/settings') ? 'tube-button-active' : ''}`}
                >
                  <Key className="w-5 h-5" />
                  <span className="hidden md:inline">API Key</span>
                </Link>
              </li>
            </ul>
          ) : (
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <Link to="/" className={`nav-link ${isActive('/') ? 'tube-button-active' : ''}`}>
                    <span className="font-medium">Accueil</span>
                  </Link>
                </NavigationMenuItem>
                <NavigationMenuItem>
                  <Link to="/support" className={`nav-link ${isActive('/support') ? 'tube-button-active' : ''}`}>
                    <span className="font-medium">Support</span>
                  </Link>
                </NavigationMenuItem>
                <NavigationMenuItem>
                  <Link to="/documentation" className={`nav-link ${isActive('/documentation') ? 'tube-button-active' : ''}`}>
                    <span className="font-medium">Documentation</span>
                  </Link>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          )}
        </nav>
        
        {/* Mobile menu overlay */}
        {isMobile && mobileMenuOpen && (
          <div className="md:hidden fixed inset-0 z-50 bg-white dark:bg-youtube-dark pt-16 pb-20">
            <nav className="flex flex-col p-4">
              {user ? (
                <ul className="flex flex-col space-y-4">
                  <li>
                    <Link 
                      to="/" 
                      className={`flex items-center gap-3 py-3 px-4 rounded-lg ${isActive('/') ? 'bg-gray-100 dark:bg-gray-800 text-youtube-red' : ''}`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Home className="w-6 h-6" />
                      <span className="font-medium">Accueil</span>
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to="/feed" 
                      className={`flex items-center gap-3 py-3 px-4 rounded-lg ${isActive('/feed') ? 'bg-gray-100 dark:bg-gray-800 text-youtube-red' : ''}`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Play className="w-6 h-6" />
                      <span className="font-medium">Flux</span>
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to="/creators" 
                      className={`flex items-center gap-3 py-3 px-4 rounded-lg ${isActive('/creators') ? 'bg-gray-100 dark:bg-gray-800 text-youtube-red' : ''}`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Users className="w-6 h-6" />
                      <span className="font-medium">Créateurs</span>
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to="/playlists" 
                      className={`flex items-center gap-3 py-3 px-4 rounded-lg ${isActive('/playlists') ? 'bg-gray-100 dark:bg-gray-800 text-youtube-red' : ''}`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Folder className="w-6 h-6" />
                      <span className="font-medium">Collections</span>
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to="/settings" 
                      className={`flex items-center gap-3 py-3 px-4 rounded-lg ${isActive('/settings') ? 'bg-gray-100 dark:bg-gray-800 text-youtube-red' : ''}`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Key className="w-6 h-6" />
                      <span className="font-medium">API Key</span>
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to="/profile" 
                      className={`flex items-center gap-3 py-3 px-4 rounded-lg ${isActive('/profile') ? 'bg-gray-100 dark:bg-gray-800 text-youtube-red' : ''}`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <User className="w-6 h-6" />
                      <span className="font-medium">Profil</span>
                    </Link>
                  </li>
                </ul>
              ) : (
                <div className="flex flex-col space-y-4 mt-4">
                  <Link 
                    to="/" 
                    className={`flex items-center gap-3 py-3 px-4 rounded-lg ${isActive('/') ? 'bg-gray-100 dark:bg-gray-800 text-youtube-red' : ''}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Home className="w-6 h-6" />
                    <span className="font-medium">Accueil</span>
                  </Link>
                  <Link 
                    to="/support" 
                    className={`flex items-center gap-3 py-3 px-4 rounded-lg ${isActive('/support') ? 'bg-gray-100 dark:bg-gray-800 text-youtube-red' : ''}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <HelpCircle className="w-6 h-6" />
                    <span className="font-medium">Support</span>
                  </Link>
                  <Link 
                    to="/documentation" 
                    className={`flex items-center gap-3 py-3 px-4 rounded-lg ${isActive('/documentation') ? 'bg-gray-100 dark:bg-gray-800 text-youtube-red' : ''}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Book className="w-6 h-6" />
                    <span className="font-medium">Documentation</span>
                  </Link>
                  <div className="border-t border-gray-200 dark:border-gray-800 pt-4 mt-4">
                    <Link 
                      to="/auth" 
                      className="bg-youtube-red text-white w-full py-3 px-4 rounded-full font-medium flex items-center justify-center"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Commencer
                    </Link>
                  </div>
                </div>
              )}
            </nav>
          </div>
        )}
        
        <div className="flex items-center">
          {user ? (
            <NavbarUserMenu />
          ) : (
            <div className="flex items-center gap-3 sm:gap-6">
              <div className="hidden sm:flex items-center">
                <Button variant="ghost-text" size="no-padding" asChild>
                  <Link to="/auth" className="text-gray-700 dark:text-white hover:text-youtube-red dark:hover:text-youtube-red font-medium transition-colors">
                    Se connecter
                  </Link>
                </Button>
              </div>
              <div className="flex items-center">
                <Button variant="youtube" size="sm" asChild className="font-medium whitespace-nowrap">
                  <Link to="/auth?tab=signup">
                    Commencer
                  </Link>
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Navbar;
